import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ListDetail } from "@/components/ListDetail";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Shell } from "@/components/Shell";
import { huidigePeriode } from "@/features/inschrijfperiodes/venster";
import { useKorpsPeriodes } from "@/features/inschrijving/useInschrijving";
import { SignIn } from "@/features/session/SignIn";
import { useMe } from "@/features/session/useMe";
import { kiesSeizoen, useSeizoenen } from "@/features/session/useSeizoenen";
import { KorpsenTable } from "@/features/ploegen/KorpsenTable";
import { PloegenDetail } from "@/features/ploegen/PloegenDetail";
import { useBeheerKorpsen } from "@/features/wedstrijden/useWedstrijden";
import { keepScrollPosition } from "@jbnl/shared-ui";

/**
 * Inschrijvingen on the korps axis, twinned with `OndersteunersPage` on the persoon axis: the
 * two are the whole of the sidebar group, which ADR-0028 keeps to namens handelen alone.
 */
export function PloegenPage() {
  const { t } = useTranslation(["paginas", "common"]);
  const { data: me, isPending: sessionLoading } = useMe();
  const { data: seizoenen } = useSeizoenen();
  // Finds the selected korps for the detail pane - independent of the list's own page, so
  // choosing one on page 1 and then paging the list to page 2 does not lose the detail pane.
  const { data: korpsen } = useBeheerKorpsen();
  const {
    korps: korpsId,
    periode: gekozenPeriodeId,
    pagina,
  } = useSearch({ from: "/ploegen" });
  const navigate = useNavigate({ from: "/ploegen" });
  // This screen carries no `seizoen` search param of its own; the one last picked on the
  // Seizoenen screen wins over the running one, same as every screen that does.
  //
  // Read above the early returns (#198): the korpsenlijst's filter needs the resolved periode
  // whether or not `PloegenDetail` is even on screen.
  const seizoen = kiesSeizoen(seizoenen);
  const { data: periodes } = useKorpsPeriodes(seizoen?.id);
  const periode =
    periodes?.find((p) => p.id === gekozenPeriodeId) ??
    huidigePeriode(periodes);

  if (sessionLoading) {
    return (
      <Shell>
        <PageSkeleton />
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell>
        <SignIn />
      </Shell>
    );
  }

  // The plain refusal every beheerder-only page gives. The fuller one - where to go instead -
  // moved to BeheerderShell with the front door: this page is no longer the one a korpscontact
  // lands on.
  if (!me.isBeheerder) {
    return (
      <Shell name={me.name} letterhead>
        <Card>
          <CardHeader>
            <CardTitle>{t("common:doors.noAccess")}</CardTitle>
            <CardDescription>
              {t("common:doors.noAccessDescription")}
            </CardDescription>
          </CardHeader>
        </Card>
      </Shell>
    );
  }

  const korps = korpsen?.find((k) => k.id === korpsId);
  const kies = (id?: string) =>
    void navigate({
      ...keepScrollPosition,
      search: (previous) => ({ ...previous, korps: id }),
    });
  const kiesPeriode = (id: string) =>
    void navigate({
      ...keepScrollPosition,
      search: (previous) => ({ ...previous, periode: id }),
    });

  return (
    <Shell name={me.name} wide>
      {/* The title above both panes rather than inside one, as on seizoenen and
          wedstrijddagen: a card wrapped around the pair cannot contain a list that has to
          scroll inside its own frame. */}
      <header className="mb-6 space-y-1">
        <h1 className="text-2xl font-bold tracking-[-0.02em]">
          {t("ploegen.titel")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {seizoen
            ? t("common:seizoen.naam", { naam: seizoen.name })
            : t("common:seizoen.geen")}
        </p>
      </header>

      {seizoen && (
        <ListDetail
          selected={Boolean(korps)}
          backLabel={t("ploegen.terug")}
          onBack={() => kies()}
          list={
            <Card>
              {/* No CardHeader: the page title above both panes already names the screen, and
                  repeating it on the list card names the same thing twice. */}
              <CardContent className="pt-6">
                <KorpsenTable
                  korpsId={korps?.id}
                  onKies={kies}
                  periodeId={periode?.id}
                  pagina={pagina}
                  onPaginaChange={(next) =>
                    void navigate({
                      search: (previous) => ({ ...previous, pagina: next }),
                      ...keepScrollPosition,
                    })
                  }
                />
              </CardContent>
            </Card>
          }
          detail={
            korps ? (
              // Keyed on the id: the detail holds the chosen periode and the ploeg its modal
              // is showing, and both belong to the korps you were looking at - so choosing
              // another one resets them by remounting rather than by an effect.
              <PloegenDetail
                key={korps.id}
                seizoenId={seizoen.id}
                korps={korps}
                gekozenPeriodeId={periode?.id}
                onKiesPeriode={kiesPeriode}
              />
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">
                    {t("ploegen.kies")}
                  </p>
                </CardContent>
              </Card>
            )
          }
        />
      )}
    </Shell>
  );
}
